import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import Badge from "../ui/badge/Badge";
import { fetchAllFilings, Filing } from "../../lib/api/wells";
import { getFilingStatuses, FilingStatus as TrackerFiling } from "../../lib/api/intelligence";

interface ActivityItem {
  id: string;
  source: "draft" | "tracker";
  form_type: string;
  status: string;
  well_name: string;
  api14: string;
  last_updated: string;
  session_id?: string;
}

function normalizeFilings(filings: Filing[]): ActivityItem[] {
  return filings.map((f) => ({
    id: f.id,
    source: "draft",
    form_type: f.form_type,
    status: f.status,
    well_name: f.lease_name ? `${f.lease_name}${f.well_number ? ` #${f.well_number}` : ""}` : "—",
    api14: f.api14 || "—",
    last_updated: f.updated_at,
    session_id: f.metadata?.session_id as string | undefined,
  }));
}

function normalizeTrackerFilings(filings: TrackerFiling[]): ActivityItem[] {
  return filings.map((f) => ({
    id: f.id,
    source: "tracker",
    form_type: f.form_type,
    status: f.status,
    well_name: f.raw_portal_data?.lease_name || "—",
    api14: f.raw_portal_data?.api_number || "—",
    last_updated: f.created_at,
  }));
}

const getStatusColor = (status: string): "success" | "warning" | "error" | "primary" | "info" => {
  const s = status.toLowerCase();
  if (s === "approved") return "success";
  if (s === "rejected" || s === "deficiency") return "error";
  if (s === "revision_requested") return "warning";
  if (s === "under_review" || s === "submitted") return "info";
  return "warning"; // draft / pending
};

const getStatusLabel = (status: string): string => {
  const map: Record<string, string> = {
    draft: "Draft",
    submitted: "Submitted",
    rejected: "Rejected",
    approved: "Approved",
    withdrawn: "Withdrawn",
    "revised and submitted": "Revised",
    pending: "Pending",
    under_review: "Under Review",
    revision_requested: "Revision Requested",
    deficiency: "Deficiency",
  };
  return map[status] ?? status;
};

const getActionLabel = (item: ActivityItem): string => {
  if (item.source === "draft" && item.status === "draft" && item.session_id) return "Continue";
  const s = item.status;
  if (s === "rejected" || s === "revision_requested" || s === "deficiency") return "Revise";
  return "View";
};

const getActionPath = (item: ActivityItem): string | null => {
  if (item.source === "draft" && item.status === "draft") {
    return item.session_id ? `/w3-wizard/${item.session_id}` : null;
  }
  if (item.status === "rejected" || item.status === "revision_requested" || item.status === "deficiency")
    return "/filing-tracker?status=revision_requested";
  return "/filing-tracker";
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export default function FilingTable() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [filingsRes, trackerRes] = await Promise.allSettled([
          fetchAllFilings({ page: 1, page_size: 20, ordering: "-updated_at" }),
          getFilingStatuses({ page_size: "20", ordering: "-created_at" }),
        ]);

        const drafts =
          filingsRes.status === "fulfilled"
            ? normalizeFilings(filingsRes.value.filings || [])
            : [];
        const tracker =
          trackerRes.status === "fulfilled"
            ? normalizeTrackerFilings(
                Array.isArray(trackerRes.value)
                  ? trackerRes.value
                  : (trackerRes.value as unknown as { results: TrackerFiling[] }).results ?? []
              )
            : [];

        const merged = [...drafts, ...tracker].sort(
          (a, b) => new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime()
        );

        setItems(merged.slice(0, 10));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load activity");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const header = (
    <div className="flex items-center justify-between px-6 py-4">
      <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">Recent Activity</h3>
      <a
        href="/filing-tracker"
        className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
      >
        View all
      </a>
    </div>
  );

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        {header}
        <div className="flex items-center justify-center py-12">
          <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-brand-500 border-r-transparent"></div>
          <p className="ml-3 text-sm text-gray-600 dark:text-gray-400">Loading activity...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        {header}
        <div className="p-6">
          <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
            <p className="text-sm font-medium text-error-700 dark:text-error-400">
              Failed to load activity: {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        {header}
        <div className="p-6 text-center">
          <p className="text-gray-600 dark:text-gray-400">No recent activity</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
      {header}
      <div className="custom-scrollbar overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-900">
              <th className="px-6 py-4 text-left text-sm font-medium whitespace-nowrap text-gray-500 dark:text-gray-400">
                Well
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium whitespace-nowrap text-gray-500 dark:text-gray-400">
                File Type
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium whitespace-nowrap text-gray-500 dark:text-gray-400">
                Last Updated
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium whitespace-nowrap text-gray-500 dark:text-gray-400">
                Status
              </th>
              <th className="px-6 py-4 text-right text-sm font-medium whitespace-nowrap text-gray-500 dark:text-gray-400">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {items.map((item) => (
              <tr key={`${item.source}-${item.id}`}>
                <td className="px-6 py-4 text-left whitespace-nowrap">
                  <span className="block text-sm font-medium text-gray-800 dark:text-white/90">
                    {item.well_name}
                  </span>
                  {item.api14 !== "—" && (
                    <span className="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {item.api14}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-left text-sm whitespace-nowrap text-gray-700 dark:text-gray-400">
                  <span className="inline-block rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                    {item.form_type}
                  </span>
                </td>
                <td className="px-6 py-4 text-left text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                  {formatDate(item.last_updated)}
                </td>
                <td className="px-6 py-4 text-left whitespace-nowrap">
                  <Badge size="sm" color={getStatusColor(item.status)}>
                    {getStatusLabel(item.status)}
                  </Badge>
                </td>
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  {getActionPath(item) !== null ? (
                    <button
                      onClick={() => navigate(getActionPath(item)!)}
                      className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 transition-colors"
                    >
                      {getActionLabel(item)}
                    </button>
                  ) : (
                    <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
